import { Component, OnInit, OnDestroy, inject, signal, computed, effect, Injector } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Firestore, collection, collectionData, doc, getDoc, query, where } from '@angular/fire/firestore';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { UserProfileDisplayService } from '../../services/user-profile-display.service';
import { ApprovalService } from '../../services/approval.service';
import { FirestoreProjectService } from '../../services/firestore-project.service';
import { DepartmentSchemesService, DepartmentEntry, SchemeCard } from '../../services/department-schemes.service';
import { PhotoUploadService } from '../../services/photo-upload.service';
import { ApprovalRequest } from '../../models/approval.model';
import { UserRole } from '../../models/user.model';
import { CompareChangesComponent } from '../../components/shared/compare-changes/compare-changes.component';
import { BulkImportModalComponent } from './bulk-import-modal/bulk-import-modal.component';
import {
  DEFAULT_OPERATOR_LOCATION_BLOCK,
  NAC_BLOCKS,
  GP_BY_BLOCK,
} from '../../constants/operator-master-data';
import { LocationMasterService } from '../../services/location-master.service';
import {
  formatBlockAccessLabel,
  projectMatchesBlockAccess,
  userHasUnrestrictedBlockAccess,
} from '../../utils/block-access.utils';
import { MasterLocation } from '../../models/location.model';
import { ProjectIdService } from '../../services/project-id.service';

@Component({
  selector: 'app-operator-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, CompareChangesComponent, BulkImportModalComponent],
  templateUrl: './operator-dashboard.component.html',
  styleUrls: ['./operator-dashboard.component.scss']
})
export class OperatorDashboardComponent implements OnInit, OnDestroy {
  private authService  = inject(AuthService);
  private route        = inject(ActivatedRoute);
  private router       = inject(Router);
  private destroy$     = new Subject<void>();
  readonly profileUi = inject(UserProfileDisplayService);
  private approvalService = inject(ApprovalService);
  private projectService = inject(FirestoreProjectService);
  private deptService = inject(DepartmentSchemesService);
  private photoUploadService = inject(PhotoUploadService);
  private firestore = inject(Firestore);
  private fb = inject(FormBuilder);
  private injector = inject(Injector);
  private projectIdService = inject(ProjectIdService);
  private locationMaster = inject(LocationMasterService);
  private departmentAssignmentSub: Subscription | null = null;
  private operatorRequestsSub: Subscription | null = null;

  /**
   * Live snapshot of approver-approved schemes from Firestore. Merged into
   * the static dept schemes so the "Select Scheme" dropdown always reflects
   * the latest approved additions across the project.
   */
  approvedSchemes = signal<Array<{ id: string; name: string; dept: string; totalCost?: number; spent?: number }>>([]);
  mappedDepartmentIds = signal<string[]>([]);

  currentUser = this.authService.currentUser;

  // Requests signals (raw stream from Firestore)
  rawRequests = signal<ApprovalRequest[]>([]);
  /** Live projects (full doc snapshots) — feeds the single-screen project table. */
  liveProjects = signal<any[]>([]);
  /** Live approved projects keyed by Firestore doc id (for projectId lookup). */
  liveProjectsById = signal<Map<string, { projectId?: string }>>(new Map());
  /** Dedicated Project ID search on the requests table. */
  projectIdSearch = signal('');

  // ── Table filter signals ──────────────────────────────────────────────────
  /** Block filter for the project table. '' = all blocks. */
  blockFilter = signal('');
  /** Executant filter for the project table. '' = all executants. */
  executantFilter = signal('');

  // ── Form block tracking (for GP dropdown reactivity) ─────────────────────
  /** Currently selected block in the project form — drives gpOptions/isNacBlock. */
  selectedFormBlock = signal<string>(DEFAULT_OPERATOR_LOCATION_BLOCK);

  // ── Multi-photo state (Before / During / After) ───────────────────────────
  photoBeforeFiles    = signal<File[]>([]);
  photoDuringFiles    = signal<File[]>([]);
  photoAfterFiles     = signal<File[]>([]);
  photoBeforeDataUrls = signal<string[]>([]);
  photoDuringDataUrls = signal<string[]>([]);
  photoAfterDataUrls  = signal<string[]>([]);
  photoBeforeExisting = signal<string[]>([]);
  photoDuringExisting = signal<string[]>([]);
  photoAfterExisting  = signal<string[]>([]);
  isUploadingPhotos   = signal(false);
  photoUploadProgress = signal(0);

  masterLocations = signal<MasterLocation[]>([]);

  /** Block options scoped to operator assignment (form + filters). */
  locationBlocks = computed(() => {
    const user = this.currentUser();
    void this.locationMaster.locations();
    return this.locationMaster.formBlockOptions(user?.blockAccess, user?.role);
  });

  pendingCount = computed(() => this.rawRequests().filter(r => r.status === 'PENDING').length);
  approvedCount = computed(() => this.rawRequests().filter(r => r.status === 'APPROVED').length);
  rejectedCount = computed(() => this.rawRequests().filter(r => r.status === 'REJECTED').length);

  /** Operator's assigned department label(s) — read-only in the project form. */
  operatorDeptDisplay = computed(() => {
    const depts = this.assignedDepts();
    if (depts.length === 0) return '';
    return depts.map(d => d.name).join(', ');
  });

  operatorBlockDisplay = computed(() => {
    const user = this.currentUser();
    return formatBlockAccessLabel(
      user?.blockAccess,
      userHasUnrestrictedBlockAccess(user?.role),
    );
  });

  /** Primary department used when the operator has one or more assignments. */
  operatorPrimaryDept = computed(() => this.assignedDepts()[0]?.name ?? '');

  /** Pre-formatted rows for the table (Submitted On as Date, approver remarks aliased). */
  myRequests = computed(() =>
    this.rawRequests().map(r => {
      const newData = (r as any).newData ?? r.payload ?? (r as any).projectData ?? {};
      const oldData = (r as any).oldData ?? {};
      const liveId =
        (r as any).createdDocId || r.targetDocId || (r as any).originalProjectId || null;
      const liveProject = liveId ? this.liveProjectsById().get(liveId) : undefined;
      const projectName =
        newData?.name || oldData?.name ||
        (r.targetCollection ? r.targetCollection : '—');
      const projectId =
        newData?.projectId ||
        oldData?.projectId ||
        liveProject?.projectId ||
        '—';
      return {
        ...r,
        projectId,
        module: projectName,
        submittedOn: this.toDate((r as any).submittedAt ?? r.createdAt),
        approverRemarks: r.remarks || ((r as any).rejectionComment) || '',
      };
    })
  );

  // Modal State
  isModalOpen = signal(false);
  isSubmitting = signal(false);
  showNewSchemeForm = signal(false);

  // ── Image upload state ──────────────────────────────────────────────────
  /** File staged for upload (not yet uploaded). */
  selectedImageFile = signal<File | null>(null);
  /** Data URL for preview (either the staged file or the existing saved URL). */
  imagePreviewUrl = signal<string | null>(null);
  /** Saved URL from the live project doc (edit mode only). */
  existingImageUrl = signal<string | null>(null);
  isUploadingImage = signal(false);
  uploadProgress   = signal(0);

  /** True while the project form is being used to EDIT an existing approved project. */
  isEditing = signal(false);
  /** Live `projects` doc id of the project currently being edited (null in create mode). */
  private editingLiveProjectId: string | null = null;
  /** Snapshot of the live project data used for change detection in the EDIT payload. */
  private editingBaseline: Record<string, any> | null = null;

  /** True while the project form is being used to edit a still-PENDING create request. */
  isEditingPendingRow = signal(false);
  /** `pending_approvals` queue doc id of the request currently being edited (null otherwise). */
  private editingPendingRequestId: string | null = null;
  /** Reserved project-id fields carried over unchanged when a pending request is edited. */
  private editingPendingIdFields: { projectId?: string; departmentCode?: string; sequenceNumber?: number } | null = null;

  // Delete-confirmation state for the "Send Delete Request" flow
  isDeleteConfirmOpen = signal(false);
  isSubmittingDelete  = signal(false);
  pendingDeleteRow    = signal<{ id: string; name: string } | null>(null);

  // Detail modal state (Compare Changes in read-only mode)
  isDetailOpen = signal(false);
  detailBefore = signal<any>(null);
  detailAfter  = signal<any>(null);
  detailMeta   = signal<any>(null);

  // Project preview modal (formatted read-only view for phantom/pending rows)
  isPreviewOpen = signal(false);
  previewRow    = signal<any>(null);

  // Bulk Import modal state
  isImportOpen = signal(false);
  openImport(): void  { this.isImportOpen.set(true); }
  onImportClosed(result: { submitted: number; failed: number } | null): void {
    this.isImportOpen.set(false);
    if (result && result.submitted > 0) {
      // Live stream will refresh the requests table; nothing extra to do.
      console.log(`[bulk-import] ${result.submitted} row(s) queued for approval${result.failed ? `, ${result.failed} failed` : ''}.`);
    }
  }

  // Post–scheme-submit prompt state (shown inside the new-project modal)
  schemeCreatedPrompt = signal<{ schemeName: string; deptName: string } | null>(null);
  /** When set, the project form's scheme is locked to this pending name. */
  pendingSchemeName = signal<string | null>(null);
  isSubmittingScheme = signal(false);

  // ── Stat card filter state ──
  /** Which stat card is currently selected (filters the table). */
  statFilter = signal<'pending' | 'approved' | 'rejected' | null>(null);
  /** Departments modal visibility. */
  isDepartmentsOpen = signal(false);

  /** Live filtered table view: stat card + Project ID search. */
  filteredRequests = computed(() => {
    const filter = this.statFilter();
    const idQuery = this.projectIdSearch().trim().toLowerCase();
    let list = this.myRequests();

    if (filter) {
      const target = filter === 'pending'  ? 'PENDING'
                   : filter === 'approved' ? 'APPROVED' : 'REJECTED';
      list = list.filter(r => r.status === target);
    }

    if (idQuery) {
      list = list.filter(r =>
        String((r as any).projectId ?? '').toLowerCase().includes(idQuery)
      );
    }

    return list;
  });

  /** Headline shown above the table — reflects the active filter. */
  filterHeadline = computed(() => {
    switch (this.statFilter()) {
      case 'pending':  return 'Pending Approval Requests';
      case 'approved': return 'Approved Requests (Last 30 Days)';
      case 'rejected': return 'Rejected Requests — Action Required';
      default:         return 'My Recent Requests';
    }
  });

  /** Live count of departments the operator is assigned to. */
  departmentsCount = computed(() => this.assignedDepts().length);

  /** GP options based on the currently selected block in the form. */
  gpOptions = computed<string[]>(() => GP_BY_BLOCK[this.selectedFormBlock()] ?? []);

  /** True when the selected block is a NAC block (GP auto-fixed to NAC). */
  isNacBlock = computed(() => NAC_BLOCKS.has(this.selectedFormBlock()));

  /** Combined before/during/after previews (existing URLs + staged data URLs). */
  photoBeforePreviews = computed(() => [...this.photoBeforeExisting(), ...this.photoBeforeDataUrls()]);
  photoDuringPreviews = computed(() => [...this.photoDuringExisting(), ...this.photoDuringDataUrls()]);
  photoAfterPreviews  = computed(() => [...this.photoAfterExisting(), ...this.photoAfterDataUrls()]);

  /** Year list for the Year of Creation dropdown (2026 down to 2020), plus the
   *  edited project's own year if it falls outside that range. */
  yearOptions = computed<number[]>(() => {
    const years: number[] = [];
    for (let y = 2026; y >= 2020; y--) years.push(y);
    const extra = this.editExtraYear();
    if (extra && !years.includes(extra)) years.unshift(extra);
    return years;
  });

  /** Unique executant names across all operator projects — used for filter dropdown. */
  executantNames = computed<string[]>(() => {
    const names = new Set<string>();
    for (const row of this.projectRows()) {
      if (row.executantName) names.add(row.executantName);
    }
    return Array.from(names).sort();
  });

  private normalizeDepartmentKey(value: string | null | undefined): string {
    return (value || '')
      .toLowerCase()
      .replace(/[\s&]/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .trim();
  }

  /** Match Firestore / import labels (e.g. PWD, works) to a master {@link DepartmentEntry}. */
  private resolveDepartmentEntry(deptLabel: string): DepartmentEntry | undefined {
    const dn = (deptLabel || '').trim();
    if (!dn) return undefined;
    const key = this.normalizeDepartmentKey(dn);
    for (const d of this.assignedDepts()) {
      if (d.name === dn || d.shortName === dn) {
        return d;
      }
      if (d.name.toLowerCase() === dn.toLowerCase() || (d.shortName && d.shortName.toLowerCase() === dn.toLowerCase())) {
        return d;
      }
      if (
        this.normalizeDepartmentKey(d.name) === key ||
        this.normalizeDepartmentKey(d.shortName || '') === key
      ) {
        return d;
      }
    }
    return undefined;
  }

  private async resolveDepartmentIdsToNames(rawIds: string[]): Promise<string[]> {
    const all = this.deptService.getDepartments();
    const normalizedToName = new Map(all.map(d => [this.normalizeDepartmentKey(d.name), d.name]));
    const normalizedShortToName = new Map(all.map(d => [this.normalizeDepartmentKey(d.shortName), d.name]));
    const resolved: string[] = [];
    const unknown: string[] = [];

    for (const raw of rawIds) {
      const key = this.normalizeDepartmentKey(raw);
      const known = normalizedToName.get(key) || normalizedShortToName.get(key);
      if (known) resolved.push(known);
      else unknown.push(raw);
    }

    if (unknown.length > 0) {
      const fetchedNames = await Promise.all(unknown.map(async (deptDocId) => {
        try {
          const snap = await getDoc(doc(this.firestore, 'departments', deptDocId));
          return snap.exists() ? String(snap.data()?.['name'] || '').trim() : '';
        } catch {
          return '';
        }
      }));
      resolved.push(...fetchedNames.filter(Boolean));
    }

    return Array.from(new Set(resolved));
  }

  // Data for forms
  assignedDepts = computed<DepartmentEntry[]>(() => {
    const user = this.currentUser();
    const allDepts = this.deptService.getDepartments();
    
    if (!user) return [];

    const rawIds = [
      ...(user.departmentIds || []),
      ...(user.assignedDepartments || []),
      ...(this.mappedDepartmentIds() || [])
    ];

    if (rawIds.length > 0) {
      // Match against both user-provided IDs and normalized department names.
      // This supports Firestore IDs, static IDs, and plain text department names.
      const allowed = new Set(rawIds.map(id => this.normalizeDepartmentKey(id)));
      
      return allDepts.filter(d => {
        const normalizedName = this.normalizeDepartmentKey(d.name);
        const normalizedShortName = this.normalizeDepartmentKey(d.shortName);
        return allowed.has(normalizedName) || allowed.has(normalizedShortName);
      });
    }

    if (user.role === UserRole.ADMIN || user.role === UserRole.OSD) {
      return allDepts;
    }

    return [];
  });
  /** The currently selected department in the project form. */
  selectedDeptName = signal<string>('');
  /** When editing, the live project's scheme may not exist in the static/Firestore lists — inject it so the select can bind. */
  editExtraSchemeName = signal<string | null>(null);
  /** When editing, the live project's year may fall outside the dropdown's normal range (e.g. an older 2027/2028 record) — inject it so the select doesn't go blank. */
  editExtraYear = signal<number | null>(null);

  /**
   * Schemes available for the selected department — combines the static
   * seed list (DepartmentSchemesService) with the live Firestore schemes
   * collection (approver-approved additions). Updates reactively when
   * either side changes.
   *
   * When {@link editExtraSchemeName} is set (edit flow), the project's current
   * scheme is prepended if missing so the &lt;select&gt; shows the approved value.
   */
  selectedDeptSchemes = computed<SchemeCard[]>(() => {
    const deptName = this.selectedDeptName().trim();
    if (!deptName) return [];

    const baseDept = this.resolveDepartmentEntry(deptName);
    const canonicalDept = baseDept?.name ?? deptName;
    const base: SchemeCard[] = baseDept ? [...baseDept.schemes] : [];

    const deptKey = this.normalizeDepartmentKey(canonicalDept);
    const knownNames = new Set(base.map(s => (s.name || '').toLowerCase()));
    const fsForDept = this.approvedSchemes().filter(s => {
      const sd = (s.dept || '').trim();
      if (!sd) return false;
      return (
        sd === canonicalDept ||
        sd === deptName ||
        this.normalizeDepartmentKey(sd) === deptKey
      );
    });
    for (const s of fsForDept) {
      if (!s.name || knownNames.has(s.name.toLowerCase())) continue;
      base.push({
        name: s.name,
        projects: 0,
        totalCost: s.totalCost ?? 0,
        spent: s.spent ?? 0,
        completed: 0,
        inProgress: 0,
        stuck: 0,
        planned: 0,
      } as SchemeCard);
      knownNames.add(s.name.toLowerCase());
    }

    const extra = this.editExtraSchemeName()?.trim();
    if (extra && !knownNames.has(extra.toLowerCase())) {
      base.unshift({
        name: extra,
        projects: 0,
        totalCost: 0,
        spent: 0,
        completed: 0,
        inProgress: 0,
        stuck: 0,
        planned: 0,
      } as SchemeCard);
    }

    return base;
  });

  projectForm: FormGroup;
  schemeForm: FormGroup;

  constructor() {
    this.projectForm = this.fb.group({
      deptName: ['', Validators.required],
      schemeName: ['', Validators.required],
      projectName: ['', [Validators.required, Validators.minLength(3)]],
      location: [DEFAULT_OPERATOR_LOCATION_BLOCK, Validators.required],
      gp: ['', Validators.required],
      yearOfCreation: ['', Validators.required],
      costCr: [null, [Validators.required, Validators.min(0.01)]],
      spentCr: [0, [Validators.min(0)]],
      status: ['Planned', Validators.required],
      hmPriority: [false],
      recurringIntervention: [false],
      executantName: [''],
      executantContact: [''],
      roadLength: [null, [Validators.min(0)]],
      photoDateBefore: [''],
      photoDateDuring: [''],
      photoDateAfter: [''],
    });

    this.schemeForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      totalCost: [0, [Validators.min(0)]],
      spent: [0, [Validators.min(0)]]
    });
  }

  ngOnInit(): void {
    void this.locationMaster.ensureLoaded().then(locs => this.masterLocations.set(locs));
    this.subscribeToOperatorRequests();
    this.subscribeToApprovedSchemes();
    this.subscribeToDepartmentAssignments();
    this.subscribeToLiveProjects();

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const action = params['action'];
      if (action === 'new-request') {
        this.openSubmitModal();
        this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
      } else if (action === 'import') {
        this.openImport();
        this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
      }
    });

    effect(() => {
      const depts = this.assignedDepts();
      if (!this.isModalOpen() || this.isEditing() || this.isEditingPendingRow() || depts.length === 0) return;
      this.applyOperatorDepartment();
      const dept = this.operatorPrimaryDept();
      if (dept) {
        this.onDeptChange(dept);
      }
    }, { injector: this.injector });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.operatorRequestsSub?.unsubscribe();
    this.departmentAssignmentSub?.unsubscribe();
    this.liveProjectsSub?.unsubscribe();
  }

  private liveProjectsSub: Subscription | null = null;

  private subscribeToLiveProjects(): void {
    this.liveProjectsSub = this.projectService.getProjects$().subscribe({
      next: projects => {
        const map = new Map<string, { projectId?: string }>();
        for (const p of projects) {
          if (p.id) {
            map.set(p.id, { projectId: (p as any).projectId });
          }
        }
        this.liveProjectsById.set(map);
        this.liveProjects.set(projects);
      },
      error: err => console.warn('[operator-dashboard] projects stream error:', err),
    });
  }

  /** Apply the operator's assigned department to the form (read-only field). */
  private applyOperatorDepartment(): void {
    const dept = this.operatorPrimaryDept();
    this.projectForm.patchValue({ deptName: dept });
    this.selectedDeptName.set(dept);
    if (!dept) {
      this.projectForm.get('deptName')?.setErrors({ notAssigned: true });
    } else {
      this.projectForm.get('deptName')?.setErrors(null);
    }
  }

  clearProjectIdSearch(): void {
    this.projectIdSearch.set('');
  }

  /**
   * Re-bind the Firestore listener whenever `currentUser()` appears or changes.
   * A one-shot subscribe in ngOnInit often ran while auth was still null, so
   * `getOperatorRequests()` returned `of([])` and never updated after refresh.
   */
  private subscribeToOperatorRequests(): void {
    effect(() => {
      const user = this.currentUser();
      this.operatorRequestsSub?.unsubscribe();
      this.operatorRequestsSub = null;

      if (!user) {
        this.rawRequests.set([]);
        return;
      }

      this.operatorRequestsSub = this.approvalService.getOperatorRequests().subscribe({
        next: requests => this.rawRequests.set(requests),
        error: err => {
          console.warn('[operator-dashboard] approval requests stream error:', err);
          this.rawRequests.set([]);
        },
      });
    }, { injector: this.injector });
  }

  /**
   * Reads legacy/normalized assignments from `departmentAssignments` so operators
   * still get department scope even when user profile fields are missing.
   */
  private subscribeToDepartmentAssignments(): void {
    effect(() => {
      const uid = this.currentUser()?.uid;
      this.departmentAssignmentSub?.unsubscribe();
      this.mappedDepartmentIds.set([]);

      if (!uid) return;
      const firebaseUid = this.authService.getFirebaseAuthUid();
      const candidateUserIds = Array.from(new Set([uid, firebaseUid].filter(Boolean) as string[]));
      if (candidateUserIds.length === 0) return;

      const assignmentsCollection = collection(this.firestore, 'departmentAssignments');
      const streams = candidateUserIds.map(candidateId => {
        const q = query(assignmentsCollection, where('userId', '==', candidateId), where('isActive', '==', true));
        return collectionData(q, { idField: 'id' }) as Observable<any[]>;
      });
      this.departmentAssignmentSub = combineLatest(streams).subscribe({
        next: async rowsPerSource => {
          const ids = rowsPerSource
            .flat()
            .map(r => (r?.departmentId || '').toString().trim())
            .filter(Boolean);
          const normalizedNames = await this.resolveDepartmentIdsToNames(Array.from(new Set(ids)));
          this.mappedDepartmentIds.set(normalizedNames);
        },
        error: err => {
          console.warn('[op-dashboard] departmentAssignments stream error:', err);
          this.mappedDepartmentIds.set([]);
        }
      });
    }, { injector: this.injector });
  }

  /**
   * Live listener on the `schemes` collection — populated whenever an
   * approver approves a new-scheme request. Any consumer (the project
   * scheme dropdown, dashboards, etc.) sees additions instantly.
   */
  private subscribeToApprovedSchemes(): void {
    const coll = collection(this.firestore, 'schemes');
    (collectionData(coll, { idField: 'id' }) as Observable<any[]>)
      .subscribe({
        next: rows => this.approvedSchemes.set(rows as any),
        error: err => console.warn('[op-dashboard] schemes stream error:', err),
      });
  }

  /** Convert Firestore Timestamp / Date / number / string → Date. */
  private toDate(t: any): Date | null {
    if (!t) return null;
    if (t instanceof Date) return t;
    if (typeof t?.toDate === 'function') return t.toDate();
    if (typeof t?.seconds === 'number')   return new Date(t.seconds * 1000);
    if (typeof t === 'number')            return new Date(t);
    if (typeof t === 'string')            return new Date(t);
    return null;
  }



  onDeptChange(deptName: string): void {
    this.editExtraSchemeName.set(null);
    this.selectedDeptName.set(deptName ?? '');
    this.projectForm.patchValue({ schemeName: '' });
  }

  toggleNewSchemeForm(): void {
    this.showNewSchemeForm.set(!this.showNewSchemeForm());
    if (this.showNewSchemeForm()) {
      this.projectForm.get('schemeName')?.clearValidators();
    } else {
      this.projectForm.get('schemeName')?.setValidators([Validators.required]);
    }
    this.projectForm.get('schemeName')?.updateValueAndValidity();
  }

  handleTableAction(event: { type: string, item: any }): void {
    if (event.type === 'view')   this.openRequestDetail(event.item);
    if (event.type === 'edit')   this.handleEdit(event.item);
    if (event.type === 'delete') this.handleDelete(event.item);
  }

  /** Resolve the live `projects` doc id from an approval row (CREATE / UPDATE). */
  private resolveLiveProjectId(row: any): string | null {
    if (!row) return null;
    return row.createdDocId
        || row.targetDocId
        || row.originalProjectId
        || null;
  }

  /** Best-effort name+dept fallback when the row predates `createdDocId` capture. */
  private async lookupProjectIdByNameDept(name?: string, dept?: string): Promise<string | null> {
    if (!name) return null;
    try {
      const list = await new Promise<any[]>((resolve, reject) => {
        const sub = this.projectService.getProjects$().subscribe({
          next: l => { resolve(l); sub.unsubscribe(); },
          error: e => { reject(e); sub.unsubscribe(); },
        });
      });
      const hit = list.find(p =>
        (p.name || '').toLowerCase() === String(name).toLowerCase() &&
        (!dept || (p.dept || '').toLowerCase() === String(dept).toLowerCase())
      );
      return hit?.id ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Edit handler — only meaningful for APPROVED project rows (operations
   * create / update). Opens the project form pre-filled with the *current*
   * live data so the operator can submit changes for re-approval.
   */
  async handleEdit(row: any): Promise<void> {
    const r = row as ApprovalRequest;
    if (r.status !== 'APPROVED') {
      alert('Edit is only available after this request has been approved.');
      return;
    }
    if (r.operation === 'delete') {
      alert('This request was a deletion — the original project is no longer in the database.');
      return;
    }

    // Resolve the live project id (covers legacy rows with no targetCollection field)
    let liveId = this.resolveLiveProjectId(r);
    if (!liveId) {
      const payload = (r as any).newData ?? r.payload ?? {};
      liveId = await this.lookupProjectIdByNameDept(payload?.name, payload?.dept);
    }
    if (!liveId) {
      alert('Could not locate the live project for this request. It may have been deleted or this was a non-project submission (e.g., scheme).');
      return;
    }

    // Pull the live project doc so the form reflects the *current* state
    let live: any = null;
    try {
      live = await this.approvalService.getTargetData('projects', liveId);
    } catch (err) {
      console.warn('[op-edit] live project fetch failed:', err);
    }
    if (!live) {
      alert('This project no longer exists in the live database.');
      return;
    }

    // Pre-fill the form from the live doc (fall back to payload values)
    const payload = (r as any).newData ?? r.payload ?? {};
    this.editingLiveProjectId = liveId;
    this.editingBaseline = { ...live };
    this.isEditing.set(true);
    this.isEditingPendingRow.set(false);
    this.editingPendingRequestId = null;
    this.editingPendingIdFields = null;
    this.showNewSchemeForm.set(false);
    this.schemeCreatedPrompt.set(null);
    this.pendingSchemeName.set(null);
    this.clearImageState();
    this.clearPhotoState();

    const rawDept = (live.dept ?? payload.dept ?? '').trim();
    const deptEntry = this.resolveDepartmentEntry(rawDept);
    const deptName = deptEntry?.name ?? rawDept;
    const schemeName = (
      live.scheme ??
      (live as any).schemeName ??
      payload.scheme ??
      (payload as any).schemeName ??
      ''
    ).trim();
    this.editExtraSchemeName.set(schemeName || null);
    this.editExtraYear.set(Number(live.yearOfCreation ?? payload.yearOfCreation) || null);

    // Load existing project image
    const savedImageUrl = (live.projectImageUrl ?? payload.projectImageUrl ?? '') as string;
    if (savedImageUrl) {
      this.existingImageUrl.set(savedImageUrl);
      this.imagePreviewUrl.set(savedImageUrl);
    }

    const loc = live.loc ?? live.block ?? payload.loc ?? payload.block ?? DEFAULT_OPERATOR_LOCATION_BLOCK;
    this.projectForm.reset({
      deptName,
      schemeName,
      projectName:    live.name    ?? payload.name    ?? '',
      location:       loc,
      gp:             live.gp     ?? payload.gp     ?? '',
      yearOfCreation: live.yearOfCreation ?? payload.yearOfCreation ?? '',
      costCr:      live.cost    ?? payload.cost    ?? null,
      spentCr:     live.spent   ?? payload.spent   ?? 0,
      status:      live.status  ?? payload.status  ?? 'Planned',
      hmPriority:            !!(live.hmPriority            ?? payload.hmPriority),
      recurringIntervention: !!(live.recurringIntervention ?? payload.recurringIntervention),
      executantName:    live.executant?.name    ?? payload.executant?.name    ?? '',
      executantContact: live.executant?.contact ?? payload.executant?.contact ?? '',
      roadLength:       live.roadLength ?? payload.roadLength ?? null,
      photoDateBefore:  live.photoDates?.before ?? payload.photoDates?.before ?? '',
      photoDateDuring:  live.photoDates?.during ?? payload.photoDates?.during ?? '',
      photoDateAfter:   live.photoDates?.after  ?? payload.photoDates?.after  ?? '',
    });
    this.selectedDeptName.set(deptName);
    this.selectedFormBlock.set(loc);
    if (NAC_BLOCKS.has(loc)) { this.projectForm.get('gp')?.disable(); }
    else                      { this.projectForm.get('gp')?.enable(); }
    // Restore project photos
    const photos = live.projectPhotos ?? payload.projectPhotos ?? {};
    this.photoBeforeExisting.set(photos.before ?? []);
    this.photoDuringExisting.set(photos.during ?? []);
    this.photoAfterExisting.set(photos.after  ?? []);
    this.isModalOpen.set(true);
  }

  /**
   * Delete handler — only meaningful for APPROVED project rows. Opens a
   * confirmation prompt; on confirm we enqueue a DELETE approval request
   * (the live project is NOT removed until the approver approves).
   */
  async handleDelete(row: any): Promise<void> {
    const r = row as ApprovalRequest;
    if (r.status !== 'APPROVED') {
      alert('Delete is only available after this request has been approved.');
      return;
    }
    if (r.operation === 'delete') {
      alert('A deletion has already been processed for this entry.');
      return;
    }

    // Resolve the live project id (covers legacy rows with no targetCollection field)
    let liveId = this.resolveLiveProjectId(r);
    if (!liveId) {
      const payload = (r as any).newData ?? r.payload ?? {};
      liveId = await this.lookupProjectIdByNameDept(payload?.name, payload?.dept);
    }
    if (!liveId) {
      alert('Could not locate the live project for this request. It may already have been deleted or this was a non-project submission (e.g., scheme).');
      return;
    }

    const payload = (r as any).newData ?? r.payload ?? {};
    this.pendingDeleteRow.set({ id: liveId, name: payload?.name || liveId });
    this.isDeleteConfirmOpen.set(true);
  }

  cancelDelete(): void {
    this.pendingDeleteRow.set(null);
    this.isDeleteConfirmOpen.set(false);
  }

  async confirmDeleteRequest(): Promise<void> {
    const target = this.pendingDeleteRow();
    if (!target) return;
    this.isSubmittingDelete.set(true);
    try {
      // For operators, `deleteProject` enqueues an approval (it does NOT delete directly).
      await this.projectService.deleteProject(target.id);
      this.isDeleteConfirmOpen.set(false);
      this.pendingDeleteRow.set(null);
    } catch (err: any) {
      console.error('Delete request failed:', err);
      alert(err?.message ?? 'Could not send the delete request. Please try again.');
    } finally {
      this.isSubmittingDelete.set(false);
    }
  }

  /** Open the request detail modal (read-only Compare Changes view). */
  openRequestDetail(row: any): void {
    const r = row as ApprovalRequest;
    const after  = (r as any).newData ?? r.payload ?? null;
    const before = (r as any).oldData ?? null;

    // Pick the most relevant timestamp for the meta strip
    const dateRaw = r.status === 'APPROVED' ? (r as any).approvedAt
                  : r.status === 'REJECTED' ? (r as any).rejectedAt
                  : (r as any).submittedAt ?? r.createdAt;

    this.detailBefore.set(before);
    this.detailAfter.set(after);
    this.detailMeta.set({
      operator:  this.profileUi.displayName(this.currentUser()) || 'You',
      operation: r.operation,
      date:      this.toDate(dateRaw) || undefined,
      status:    r.status,
      remarks:   r.remarks || (r as any).rejectionComment || '',
    });
    this.isDetailOpen.set(true);
  }

  closeRequestDetail(): void {
    this.isDetailOpen.set(false);
  }

  // ── Stat-card popup modal ─────────────────────────────────────────
  /** Which stat-card popup is open (null = closed). Drives the modal markup. */
  statModalFilter = signal<'pending' | 'approved' | 'rejected' | null>(null);

  /** Click on a stat card → open the popup with the matching filtered list. */
  selectStat(filter: 'pending' | 'approved' | 'rejected'): void {
    this.statModalFilter.set(filter);
  }

  closeStatModal(): void {
    this.statModalFilter.set(null);
  }

  /** Friendly title for the popup header. */
  statModalTitle = computed(() => {
    switch (this.statModalFilter()) {
      case 'pending':  return 'Projects with Pending Approval';
      case 'approved': return 'Approved Projects';
      case 'rejected': return 'Rejected / Returned Projects';
      default:         return '';
    }
  });

  /** Live rows for the popup, derived from the same `projectRows()` source as the cards. */
  statModalRows = computed<any[]>(() => {
    const filter = this.statModalFilter();
    if (!filter) return [];
    const target = filter === 'pending'  ? 'PENDING'
                 : filter === 'approved' ? 'APPROVED' : 'REJECTED';
    return this.projectRows().filter(r => r.approvalStatus === target);
  });

  /** Legacy no-op kept so the template still compiles if referenced. */
  clearStatFilter(): void { this.statFilter.set(null); }

  /**
   * Delete from the stat-card popup. Behaviour depends on row type:
   *  • Phantom row backed by a pending/rejected request → withdraw the request
   *    (deletes the queue doc; nothing else moves).
   *  • Live project row with no pending request → fall through to the regular
   *    DELETE-approval flow (opens the confirmation modal).
   *  • Live project row WITH a pending edit/delete request → withdraw that
   *    request, leaving the live project untouched.
   */
  async deleteStatRow(row: any): Promise<void> {
    const req = row?.pendingRequest;
    const status = row?.approvalStatus;
    const label = row?.name || 'this entry';

    // Live approved project with no in-flight request → enqueue a DELETE approval
    if (!row?.isPhantom && status === 'APPROVED' && !req) {
      this.closeStatModal();
      this.deleteProjectRow(row);
      return;
    }

    // Otherwise we are withdrawing a queue doc the operator owns
    if (!req?.id) {
      alert('Nothing to delete for this row.');
      return;
    }
    const action = status === 'PENDING' ? 'withdraw' : 'remove';
    if (!confirm(`Are you sure you want to ${action} the ${status.toLowerCase()} request for "${label}"?`)) {
      return;
    }
    try {
      await this.approvalService.withdrawRequest(req.id);
      // Live streams will refresh the popup automatically — no manual close needed
    } catch (err: any) {
      console.error('[op-popup] withdraw failed:', err);
      alert(err?.message ?? 'Could not withdraw this request. Please try again.');
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Single-screen project table (replaces "My Recent Requests")
  // ═══════════════════════════════════════════════════════════════════

  /** Free-text search (project name / scheme / district / block / project ID). */
  projectSearch = signal('');
  /** Project-status filter (Planned / In Progress / Completed / Stuck / ''). */
  projectStatusFilter = signal('');

  /** Live projects that belong to assigned department(s) and Block/NAC. */
  operatorProjects = computed<any[]>(() => {
    const user = this.currentUser();
    const depts = this.assignedDepts();
    const all = this.liveProjects();
    const locations = this.masterLocations();

    let filtered = all;
    if (depts.length > 0) {
      const allowed = new Set(depts.map(d => this.normalizeDepartmentKey(d.name)));
      filtered = all.filter(p => allowed.has(this.normalizeDepartmentKey(p.dept)));
    } else if (user?.role !== UserRole.ADMIN) {
      return [];
    }

    if (userHasUnrestrictedBlockAccess(user?.role)) return filtered;
    if (!user?.blockAccess?.length) {
      return user?.role === UserRole.ADMIN ? filtered : [];
    }
    return filtered.filter(p =>
      projectMatchesBlockAccess(p, user.blockAccess, locations)
    );
  });

  /**
   * Build a flat row list:
   *   1. Every LIVE project in the operator's dept(s) — with its approval state
   *      derived from the most recent in-flight request (if any).
   *   2. Plus phantom rows for pending / rejected CREATE requests that haven't
   *      produced a live document yet, so the operator can still see them.
   */
  projectRows = computed<any[]>(() => {
    const live      = this.operatorProjects();
    const requests  = this.rawRequests();

    // Most-recent request per live project id (so EDIT/DELETE pending shows on the live row)
    const byDocId = new Map<string, ApprovalRequest>();
    for (const r of requests) {
      const ids = [
        r.targetDocId,
        (r as any).createdDocId,
        (r as any).originalProjectId,
      ].filter(Boolean) as string[];
      for (const id of ids) {
        const prev = byDocId.get(id);
        if (!prev || this.tsMs((r as any).createdAt) > this.tsMs((prev as any).createdAt)) {
          byDocId.set(id, r);
        }
      }
    }

    const rows: any[] = live.map(p => {
      const req = byDocId.get(p.id);
      const reqStatus = req ? this.reqStatus(req) : null;
      const approvalStatus = reqStatus === 'PENDING'  ? 'PENDING'
                           : reqStatus === 'REJECTED' ? 'REJECTED'
                           : 'APPROVED';                              // live = previously approved
      return {
        id:              p.id,
        projectId:       p.projectId ?? null,
        name:            p.name ?? p.roadName ?? '—',
        scheme:          p.scheme ?? '—',
        district:        p.district ?? p.loc ?? '—',
        block:           p.block ?? p.division ?? p.loc ?? '—',
        gp:              p.gp ?? '',
        yearOfCreation:  p.yearOfCreation ?? '',
        lengthKm:        p.roadLength ?? p.lengthKm ?? null,
        costLakh:        p.cost ?? p.costLakh ?? 0,
        spentLakh:       p.spent ?? p.spentLakh ?? 0,
        status:          p.status ?? 'Planned',
        progress:        Number(p.physical ?? p.progress ?? 0),
        updatedAt:       p.updatedAt ?? p.createdAt ?? null,
        dept:            p.dept,
        hmPriority:            !!p.hmPriority,
        recurringIntervention: !!p.recurringIntervention,
        executantName:   (p.executant?.name  || '').toString(),
        executantContact:(p.executant?.contact || '').toString(),
        projectPhotos:   p.projectPhotos ?? null,
        photoDates:      p.photoDates ?? null,
        approvalStatus,                                                // PENDING | APPROVED | REJECTED
        pendingRequest:  req && this.reqStatus(req) === 'PENDING' ? req : null,
        isPhantom:       false,
        rawLive:         p,
      };
    });

    // Phantom rows: pending / rejected CREATE requests that haven't produced a live doc.
    const liveIds = new Set(live.map(p => p.id));
    for (const r of requests) {
      if (r.operation !== 'create') continue;
      const st = this.reqStatus(r);
      if (st !== 'PENDING' && st !== 'REJECTED') continue;
      const docId = r.targetDocId || (r as any).createdDocId;
      if (docId && liveIds.has(docId)) continue;                       // already represented by live row
      const payload = (r as any).newData ?? r.payload ?? {};
      const user = this.currentUser();
      if (
        !userHasUnrestrictedBlockAccess(user?.role) &&
        user?.blockAccess?.length &&
        !projectMatchesBlockAccess(
          { block: payload.block ?? payload.division ?? payload.loc },
          user.blockAccess,
          this.masterLocations(),
        )
      ) {
        continue;
      }
      rows.push({
        id:              docId ?? `req-${r.id}`,
        projectId:       (payload as any).projectId ?? null,
        name:            payload.name ?? payload.roadName ?? '—',
        scheme:          payload.scheme ?? '—',
        district:        payload.district ?? payload.loc ?? '—',
        block:           payload.block ?? payload.division ?? payload.loc ?? '—',
        gp:              payload.gp ?? '',
        yearOfCreation:  payload.yearOfCreation ?? '',
        lengthKm:        payload.roadLength ?? payload.lengthKm ?? null,
        costLakh:        payload.cost ?? payload.costLakh ?? 0,
        spentLakh:       payload.spent ?? payload.spentLakh ?? 0,
        status:          payload.status ?? 'Planned',
        progress:        Number(payload.physical ?? 0),
        updatedAt:       (r as any).submittedAt ?? r.createdAt,
        dept:            payload.dept,
        hmPriority:            !!payload.hmPriority,
        recurringIntervention: !!payload.recurringIntervention,
        executantName:   (payload.executant?.name  || '').toString(),
        executantContact:(payload.executant?.contact || '').toString(),
        projectPhotos:   payload.projectPhotos ?? null,
        photoDates:      payload.photoDates ?? null,
        approvalStatus:  st,
        pendingRequest:  st === 'PENDING' ? r : null,
        isPhantom:       true,
        rawLive:         null,
      });
    }

    return rows;
  });

  /** Three-stat counters (clickable cards). */
  pendingProjectsCount  = computed(() => this.projectRows().filter(r => r.approvalStatus === 'PENDING').length);
  approvedProjectsCount = computed(() => this.projectRows().filter(r => r.approvalStatus === 'APPROVED').length);
  rejectedProjectsCount = computed(() => this.projectRows().filter(r => r.approvalStatus === 'REJECTED').length);
  /** Rows eligible for the main table (excludes pending — those live in the Pending popup). */
  tableBaseRowsCount = computed(() => this.projectRows().filter(r => r.approvalStatus !== 'PENDING').length);

  /** Filtered + sorted view honouring search, status, block and executant filters.
   *  Pending rows are intentionally excluded — they live only in the Pending popup. */
  filteredProjectRows = computed<any[]>(() => {
    const q     = this.projectSearch().trim().toLowerCase();
    const sfilt = this.projectStatusFilter();
    const bfilt = this.blockFilter();
    const efilt = this.executantFilter();

    return this.projectRows().filter(row => {
      // Only show approved projects in the main table.
      // Pending rows (awaiting approver action) are visible only in the Pending popup.
      if (row.approvalStatus === 'PENDING') return false;

      if (sfilt && (row.status ?? 'Planned') !== sfilt) return false;
      if (bfilt && row.block !== bfilt) return false;
      if (efilt && row.executantName !== efilt) return false;

      if (q) {
        const hay = [
          row.name, row.scheme, row.block, row.gp,
          row.projectId, row.id, row.executantName,
        ].filter(v => v !== undefined && v !== null)
         .map(v => String(v).toLowerCase()).join(' ');
        if (!hay.includes(q)) return false;
      }
      return true;
    }).sort((a: any, b: any) => this.tsMs(b.updatedAt) - this.tsMs(a.updatedAt));
  });

  /** Headline above the table — stat-card clicks open a popup, so this is fixed. */
  projectFilterHeadline = computed(() => 'My Projects');

  // ── Pagination ─────────────────────────────────────────────────────
  opPage     = signal(1);
  opPageSize = signal<number>(25);

  get opTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredProjectRows().length / this.opPageSize()));
  }
  get opPagedProjects(): any[] {
    const list = this.filteredProjectRows();
    const max  = Math.max(1, Math.ceil(list.length / this.opPageSize()));
    if (this.opPage() > max) queueMicrotask(() => this.opPage.set(max));
    const safePage = Math.min(this.opPage(), max);
    const start    = (safePage - 1) * this.opPageSize();
    return list.slice(start, start + this.opPageSize());
  }
  opSlForIndex(i: number): number {
    return (this.opPage() - 1) * this.opPageSize() + i + 1;
  }
  opGoToPage(p: number): void {
    const max = this.opTotalPages;
    this.opPage.set(Math.max(1, Math.min(p, max)));
  }
  opPrevPage(): void { this.opGoToPage(this.opPage() - 1); }
  opNextPage(): void { this.opGoToPage(this.opPage() + 1); }
  opSetPageSize(size: number | string): void {
    const n = Number(size) || 25;
    this.opPageSize.set(n);
    this.opPage.set(1);
  }
  get opPageRange(): string {
    const total = this.filteredProjectRows().length;
    if (total === 0) return '0 of 0';
    const start = (this.opPage() - 1) * this.opPageSize() + 1;
    const end   = Math.min(start + this.opPageSize() - 1, total);
    return `${start}–${end} of ${total}`;
  }

  opResetTableFilters(): void {
    this.projectSearch.set('');
    this.projectStatusFilter.set('');
    this.blockFilter.set('');
    this.executantFilter.set('');
    this.statFilter.set(null);
    this.opPage.set(1);
  }

  /** Coerce Firestore Timestamp / Date / string / number → epoch ms. */
  private tsMs(t: any): number {
    if (!t) return 0;
    if (typeof t?.toDate === 'function') return t.toDate().getTime();
    if (typeof t?.seconds === 'number')   return t.seconds * 1000;
    if (t instanceof Date)                return t.getTime();
    if (typeof t === 'number')            return t;
    if (typeof t === 'string')            return Date.parse(t) || 0;
    return 0;
  }

  /** Normalise queue status across legacy (`status`) and spec (`approvalStatus`) fields. */
  private reqStatus(r: any): 'PENDING' | 'APPROVED' | 'REJECTED' {
    const s = String(r?.status ?? r?.approvalStatus ?? '').toUpperCase();
    if (s === 'PENDING' || s === 'APPROVED' || s === 'REJECTED') return s as any;
    // Default to PENDING so missing-field legacy rows still surface in dashboards.
    return 'PENDING';
  }

  /** Format an updatedAt-style value for the table. */
  lastUpdatedOf(row: any): string {
    const ms = this.tsMs(row?.updatedAt);
    if (!ms) return '—';
    return new Date(ms).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  }

  /** Status-pill CSS class — matches the dept-page convention. */
  projectStatusClass(status: string | undefined): string {
    switch (status) {
      case 'Completed':   return 'op-pill op-completed';
      case 'In Progress': return 'op-pill op-inprogress';
      case 'Stuck':       return 'op-pill op-stuck';
      case 'Planned':     return 'op-pill op-planned';
      default:            return 'op-pill op-planned';
    }
  }

  /** Approval-state pill class for the table's approval column. */
  approvalPillClass(status: string): string {
    switch (status) {
      case 'PENDING':  return 'op-pill op-pending';
      case 'APPROVED': return 'op-pill op-approved';
      case 'REJECTED': return 'op-pill op-rejected';
      default:         return 'op-pill';
    }
  }

  // ── Project-row action handlers (Edit / Delete) ───────────────────
  /** Open the New Project / Edit form pre-filled from a live project row. */
  async editProjectRow(row: any): Promise<void> {
    if (row.isPhantom) {
      alert('This project is still waiting for approval — wait until it is approved before editing.');
      return;
    }
    if (row.pendingRequest) {
      alert('An edit/delete request is already pending for this project. Wait for approver action.');
      return;
    }
    const liveId = row.id;
    if (!liveId) {
      alert('Could not locate the live project document.');
      return;
    }
    let live: any = null;
    try {
      live = await this.approvalService.getTargetData('projects', liveId);
    } catch (err) {
      console.warn('[op-edit-project] live fetch failed:', err);
    }
    if (!live) {
      alert('This project no longer exists in the live database.');
      return;
    }

    // Pre-fill the existing project form modal
    this.editingLiveProjectId = liveId;
    this.editingBaseline = { ...live };
    this.isEditing.set(true);
    this.isEditingPendingRow.set(false);
    this.editingPendingRequestId = null;
    this.editingPendingIdFields = null;
    this.showNewSchemeForm.set(false);
    this.schemeCreatedPrompt.set(null);
    this.pendingSchemeName.set(null);
    this.clearImageState();

    const rawDept = (live.dept ?? '').trim();
    const deptEntry = this.resolveDepartmentEntry(rawDept);
    const deptName = deptEntry?.name ?? rawDept;
    const schemeName = (live.scheme ?? (live as any).schemeName ?? '').trim();
    this.editExtraSchemeName.set(schemeName || null);
    this.editExtraYear.set(Number(live.yearOfCreation) || null);

    // Load existing project image
    const savedImageUrl = (live.projectImageUrl ?? '') as string;
    if (savedImageUrl) {
      this.existingImageUrl.set(savedImageUrl);
      this.imagePreviewUrl.set(savedImageUrl);
    }

    const loc = live.loc ?? live.block ?? DEFAULT_OPERATOR_LOCATION_BLOCK;
    this.projectForm.reset({
      deptName,
      schemeName,
      projectName:    live.name ?? '',
      location:       loc,
      gp:             live.gp ?? '',
      yearOfCreation: live.yearOfCreation ?? '',
      costCr:      live.cost  ?? null,
      spentCr:     live.spent ?? 0,
      status:      live.status ?? 'Planned',
      hmPriority:            !!live.hmPriority,
      recurringIntervention: !!live.recurringIntervention,
      executantName:    live.executant?.name    ?? '',
      executantContact: live.executant?.contact ?? '',
      roadLength:       live.roadLength ?? null,
      photoDateBefore:  live.photoDates?.before ?? '',
      photoDateDuring:  live.photoDates?.during ?? '',
      photoDateAfter:   live.photoDates?.after  ?? '',
    });
    this.selectedDeptName.set(deptName);
    this.selectedFormBlock.set(loc);
    if (NAC_BLOCKS.has(loc)) { this.projectForm.get('gp')?.disable(); }
    else                      { this.projectForm.get('gp')?.enable(); }
    // Restore project photos
    const photos = live.projectPhotos ?? {};
    this.photoBeforeExisting.set(photos.before ?? []);
    this.photoDuringExisting.set(photos.during ?? []);
    this.photoAfterExisting.set(photos.after  ?? []);
    this.isModalOpen.set(true);
  }

  /**
   * Edit a project that is still awaiting its FIRST approval (phantom row,
   * no live doc yet). Pre-fills the form from the queued request's payload;
   * submitRequest() will withdraw the old request and re-enqueue a corrected
   * one carrying the same reserved project id forward.
   */
  editPendingRequest(row: any): void {
    if (!row?.isPhantom || row.approvalStatus !== 'PENDING' || !row.pendingRequest) {
      alert('Only your own pending (not yet approved) projects can be edited this way.');
      return;
    }
    const req: any = row.pendingRequest;
    const payload = req.newData ?? req.payload ?? req.projectData ?? {};

    this.editingPendingRequestId = req.id;
    this.editingPendingIdFields = {
      projectId:      payload.projectId,
      departmentCode: payload.departmentCode,
      sequenceNumber: payload.sequenceNumber,
    };
    this.isEditingPendingRow.set(true);
    this.isEditing.set(false);
    this.editingLiveProjectId = null;
    this.editingBaseline = null;
    this.showNewSchemeForm.set(false);
    this.schemeCreatedPrompt.set(null);
    this.pendingSchemeName.set(null);
    this.clearImageState();
    this.clearPhotoState();
    this.closeProjectPreview();

    const rawDept = (payload.dept ?? '').trim();
    const deptEntry = this.resolveDepartmentEntry(rawDept);
    const deptName = deptEntry?.name ?? rawDept;
    const schemeName = (payload.scheme ?? payload.schemeName ?? '').trim();
    this.editExtraSchemeName.set(schemeName || null);
    this.editExtraYear.set(Number(payload.yearOfCreation) || null);

    const savedImageUrl = (payload.projectImageUrl ?? '') as string;
    if (savedImageUrl) {
      this.existingImageUrl.set(savedImageUrl);
      this.imagePreviewUrl.set(savedImageUrl);
    }

    const loc = payload.loc ?? payload.block ?? DEFAULT_OPERATOR_LOCATION_BLOCK;
    this.projectForm.reset({
      deptName,
      schemeName,
      projectName:    payload.name ?? '',
      location:       loc,
      gp:             payload.gp ?? '',
      yearOfCreation: payload.yearOfCreation ?? '',
      costCr:      payload.cost  ?? null,
      spentCr:     payload.spent ?? 0,
      status:      payload.status ?? 'Planned',
      hmPriority:            !!payload.hmPriority,
      recurringIntervention: !!payload.recurringIntervention,
      executantName:    payload.executant?.name    ?? '',
      executantContact: payload.executant?.contact ?? '',
      roadLength:       payload.roadLength ?? null,
      photoDateBefore:  payload.photoDates?.before ?? '',
      photoDateDuring:  payload.photoDates?.during ?? '',
      photoDateAfter:   payload.photoDates?.after  ?? '',
    });
    this.selectedDeptName.set(deptName);
    this.selectedFormBlock.set(loc);
    if (NAC_BLOCKS.has(loc)) { this.projectForm.get('gp')?.disable(); }
    else                      { this.projectForm.get('gp')?.enable(); }
    const photos = payload.projectPhotos ?? {};
    this.photoBeforeExisting.set(photos.before ?? []);
    this.photoDuringExisting.set(photos.during ?? []);
    this.photoAfterExisting.set(photos.after  ?? []);
    this.isModalOpen.set(true);
  }

  /** Open the Delete confirmation modal for a live project row. */
  deleteProjectRow(row: any): void {
    if (row.isPhantom) {
      alert('This project is still pending approval — there is nothing live to delete yet.');
      return;
    }
    if (row.pendingRequest) {
      alert('An edit/delete request is already pending for this project. Wait for approver action.');
      return;
    }
    this.pendingDeleteRow.set({ id: row.id, name: row.name || row.id });
    this.isDeleteConfirmOpen.set(true);
  }

  /** Open a read-only details modal for any row (live or phantom). */
  viewProjectRow(row: any): void {
    if (row.isPhantom) {
      // Phantom rows (pending/rejected creates) — show formatted preview instead of raw JSON
      this.openProjectPreview(row);
      return;
    }
    // For live rows navigate to the full project details page
    const id = row.id ?? row.firestoreId ?? row.docId;
    if (id) {
      this.router.navigate(['/dhamnagar-dashboard/project', id]);
      return;
    }
    // Fallback: formatted preview when no Firestore id is available
    this.openProjectPreview(row);
  }

  openProjectPreview(row: any): void {
    this.previewRow.set(row);
    this.isPreviewOpen.set(true);
  }

  closeProjectPreview(): void {
    this.isPreviewOpen.set(false);
    this.previewRow.set(null);
  }

  /** Photo arrays from the preview row. */
  previewPhotosBefore  = computed<string[]>(() => this.previewRow()?.projectPhotos?.before  ?? []);
  previewPhotosDuring  = computed<string[]>(() => this.previewRow()?.projectPhotos?.during  ?? []);
  previewPhotosAfter   = computed<string[]>(() => this.previewRow()?.projectPhotos?.after   ?? []);
  previewDateBefore    = computed<string | null>(() => this.previewRow()?.photoDates?.before ?? null);
  previewDateDuring    = computed<string | null>(() => this.previewRow()?.photoDates?.during ?? null);
  previewDateAfter     = computed<string | null>(() => this.previewRow()?.photoDates?.after  ?? null);

  openDepartments(): void  { this.isDepartmentsOpen.set(true); }
  closeDepartments(): void { this.isDepartmentsOpen.set(false); }

  // ── Image upload handlers ───────────────────────────────────────────────

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert('Only JPG, PNG, and WEBP images are supported.');
      input.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5 MB.');
      input.value = '';
      return;
    }

    this.selectedImageFile.set(file);
    const reader = new FileReader();
    reader.onload = (e) => this.imagePreviewUrl.set(e.target?.result as string);
    reader.readAsDataURL(file);
    input.value = '';
  }

  removeImage(): void {
    this.selectedImageFile.set(null);
    this.imagePreviewUrl.set(null);
    this.existingImageUrl.set(null);
  }

  private clearImageState(): void {
    this.selectedImageFile.set(null);
    this.imagePreviewUrl.set(null);
    this.existingImageUrl.set(null);
    this.isUploadingImage.set(false);
    this.uploadProgress.set(0);
  }

  /** Clear all multi-photo state (before/during/after). */
  private clearPhotoState(): void {
    this.photoBeforeFiles.set([]);
    this.photoDuringFiles.set([]);
    this.photoAfterFiles.set([]);
    this.photoBeforeDataUrls.set([]);
    this.photoDuringDataUrls.set([]);
    this.photoAfterDataUrls.set([]);
    this.photoBeforeExisting.set([]);
    this.photoDuringExisting.set([]);
    this.photoAfterExisting.set([]);
    this.isUploadingPhotos.set(false);
    this.photoUploadProgress.set(0);
  }

  /** Handle file selection for before/during/after photo categories. */
  onPhotoSelected(category: 'before' | 'during' | 'after', event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    const valid = files.filter(f => allowed.includes(f.type) && f.size <= 5 * 1024 * 1024);
    if (valid.length < files.length) {
      alert('Some files skipped — only JPG, PNG, WEBP under 5MB are allowed.');
    }
    const filesSignal    = category === 'before' ? this.photoBeforeFiles    : category === 'during' ? this.photoDuringFiles    : this.photoAfterFiles;
    const dataUrlsSignal = category === 'before' ? this.photoBeforeDataUrls : category === 'during' ? this.photoDuringDataUrls : this.photoAfterDataUrls;
    filesSignal.update(arr => [...arr, ...valid]);
    valid.forEach(file => {
      const reader = new FileReader();
      reader.onload = e => dataUrlsSignal.update(arr => [...arr, e.target?.result as string]);
      reader.readAsDataURL(file);
    });
  }

  /** Remove a photo from a category. Index covers existing + staged together. */
  removePhoto(category: 'before' | 'during' | 'after', index: number): void {
    const existingSignal = category === 'before' ? this.photoBeforeExisting : category === 'during' ? this.photoDuringExisting : this.photoAfterExisting;
    const filesSignal    = category === 'before' ? this.photoBeforeFiles    : category === 'during' ? this.photoDuringFiles    : this.photoAfterFiles;
    const dataUrlsSignal = category === 'before' ? this.photoBeforeDataUrls : category === 'during' ? this.photoDuringDataUrls : this.photoAfterDataUrls;
    const existingCount = existingSignal().length;
    if (index < existingCount) {
      existingSignal.update(arr => arr.filter((_, i) => i !== index));
    } else {
      const si = index - existingCount;
      filesSignal.update(arr => arr.filter((_, i) => i !== si));
      dataUrlsSignal.update(arr => arr.filter((_, i) => i !== si));
    }
  }

  /** Upload all staged files for a single category; returns array of download URLs. */
  private async uploadPhotoCategory(projectId: string, category: 'before' | 'during' | 'after', files: File[]): Promise<string[]> {
    const urls: string[] = [];
    for (const file of files) {
      const url = await this.photoUploadService.uploadPhoto(`${projectId}/${category}`, file);
      urls.push(url);
    }
    return urls;
  }

  /** Handler called when the block select changes (form). Updates GP dropdown. */
  onBlockChange(block: string): void {
    this.selectedFormBlock.set(block);
    const gpCtrl = this.projectForm.get('gp');
    if (NAC_BLOCKS.has(block)) {
      gpCtrl?.setValue('NAC');
      gpCtrl?.disable();
    } else {
      gpCtrl?.enable();
      gpCtrl?.setValue('');
    }
  }

  /** Derive a short display name for a scheme (extracts acronym from parentheses or takes initials). */
  schemeShortName(name: string): string {
    if (!name || name === '—') return name;
    const m = name.match(/\(([A-Z0-9]{2,10})\)/);
    if (m) return m[1];
    if (name.length <= 8 || /^[A-Z0-9\-]+$/.test(name)) return name;
    const initials = name.split(/[\s\-\/&]+/).filter(w => w.length > 1).map(w => w[0].toUpperCase()).join('');
    return initials || name.slice(0, 8);
  }

  openSubmitModal(): void {
    void this.locationMaster.ensureLoaded().then(locs => {
      this.masterLocations.set(locs);
      this.isEditing.set(false);
      this.editingLiveProjectId = null;
      this.editingBaseline = null;
      this.isEditingPendingRow.set(false);
      this.editingPendingRequestId = null;
      this.editingPendingIdFields = null;
      this.editExtraSchemeName.set(null);
      this.editExtraYear.set(null);
      this.selectedDeptName.set('');
      const defaultBlock = this.locationBlocks()[0] ?? DEFAULT_OPERATOR_LOCATION_BLOCK;
      this.projectForm.reset({
        location: defaultBlock,
        gp: '',
        yearOfCreation: '',
        status: 'Planned',
        costCr: null,
        spentCr: 0,
        hmPriority: false,
        recurringIntervention: false,
        deptName: '',
        schemeName: '',
        projectName: '',
        executantName: '',
        executantContact: '',
        roadLength: null,
        photoDateBefore: '',
        photoDateDuring: '',
        photoDateAfter: '',
      });
      this.projectForm.get('gp')?.enable();
      this.selectedFormBlock.set(defaultBlock);
      this.applyOperatorDepartment();
      this.schemeForm.reset({ name: '', totalCost: 0, spent: 0 });
      this.showNewSchemeForm.set(false);
      this.schemeCreatedPrompt.set(null);
      this.pendingSchemeName.set(null);
      this.clearImageState();
      this.clearPhotoState();
      this.isModalOpen.set(true);
    });
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.isEditing.set(false);
    this.editingLiveProjectId = null;
    this.editingBaseline = null;
    this.isEditingPendingRow.set(false);
    this.editingPendingRequestId = null;
    this.editingPendingIdFields = null;
    this.editExtraSchemeName.set(null);
    this.editExtraYear.set(null);
    this.schemeCreatedPrompt.set(null);
    this.pendingSchemeName.set(null);
    this.clearImageState();
    this.clearPhotoState();
  }

  /**
   * Submit ONLY the new scheme for approval (no project attached).
   * Operator can then choose to also queue a project under it.
   */
  async submitSchemeOnly(): Promise<void> {
    if (this.schemeForm.invalid) {
      this.schemeForm.markAllAsTouched();
      return;
    }
    const deptName = this.operatorPrimaryDept();
    if (!deptName) {
      alert('No department is assigned to your account. Contact an administrator.');
      return;
    }
    this.projectForm.patchValue({ deptName });
    this.isSubmittingScheme.set(true);
    try {
      const s = this.schemeForm.value;
      await this.approvalService.enqueueChange({
        targetCollection: 'schemes',
        operation: 'create',
        payload: {
          name:       s.name,
          dept:       deptName,
          totalCost:  Number(s.totalCost) || 0,
          spent:      Number(s.spent) || 0,
        },
        remarks: 'New scheme creation request',
      });
      // Show the post-submit prompt inside the modal
      this.schemeCreatedPrompt.set({ schemeName: s.name, deptName });
    } catch (err: any) {
      console.error('[scheme-only submit] failed:', err);
      alert(err?.message ?? 'Could not submit the scheme. Please try again.');
    } finally {
      this.isSubmittingScheme.set(false);
    }
  }

  /** "Yes, add a project under this scheme" — locks the new scheme name and
   *  flips the form back to project-entry mode. */
  continueWithProjectUnderNewScheme(): void {
    const p = this.schemeCreatedPrompt();
    if (!p) return;
    this.showNewSchemeForm.set(false);
    this.pendingSchemeName.set(p.schemeName);
    this.projectForm.patchValue({
      deptName:   p.deptName,
      schemeName: p.schemeName,
    });
    this.onDeptChange(p.deptName);
    this.schemeCreatedPrompt.set(null);
  }

  /** "No, that's all" — closes the modal. */
  declineProjectAndClose(): void {
    this.schemeCreatedPrompt.set(null);
    this.closeModal();
  }

  async submitRequest(): Promise<void> {
    const deptName = this.operatorPrimaryDept();
    if (!deptName) {
      alert('No department is assigned to your account. Contact an administrator.');
      return;
    }
    this.projectForm.patchValue({ deptName });

    // ── Pending-edit branch: withdraw the old queued request and re-enqueue
    //    a corrected CREATE request carrying the same reserved project id ──
    if (this.editingPendingRequestId) {
      if (this.projectForm.invalid) {
        this.projectForm.markAllAsTouched();
        return;
      }
      this.isSubmitting.set(true);
      try {
        const projData = this.projectForm.getRawValue();
        const idFields = this.editingPendingIdFields ?? {};

        const beforeUrls = [...this.photoBeforeExisting()];
        const duringUrls = [...this.photoDuringExisting()];
        const afterUrls  = [...this.photoAfterExisting()];
        const allNewPhotos = [
          ...this.photoBeforeFiles().map(f => ({ cat: 'before' as const, f })),
          ...this.photoDuringFiles().map(f => ({ cat: 'during' as const, f })),
          ...this.photoAfterFiles().map(f => ({ cat: 'after'  as const, f })),
        ];
        if (allNewPhotos.length) {
          this.isUploadingPhotos.set(true);
          this.photoUploadProgress.set(0);
          const idForUpload = idFields.projectId || 'pending-edit';
          for (let i = 0; i < allNewPhotos.length; i++) {
            const { cat, f } = allNewPhotos[i];
            const url = await this.photoUploadService.uploadPhoto(`${idForUpload}/${cat}`, f);
            if (cat === 'before') beforeUrls.push(url);
            else if (cat === 'during') duringUrls.push(url);
            else afterUrls.push(url);
            this.photoUploadProgress.set(Math.round(((i + 1) / allNewPhotos.length) * 100));
          }
          this.isUploadingPhotos.set(false);
        }

        const updatedPayload: Record<string, any> = {
          name:                  projData.projectName,
          dept:                  projData.deptName,
          scheme:                projData.schemeName,
          ...idFields,
          loc:                   projData.location,
          block:                 projData.location,
          gp:                    projData.gp ?? '',
          yearOfCreation:        projData.yearOfCreation ?? '',
          cost:                  Number(projData.costCr)  || 0,
          spent:                 Number(projData.spentCr) || 0,
          status:                projData.status,
          hmPriority:            !!projData.hmPriority,
          recurringIntervention: !!projData.recurringIntervention,
          roadLength: projData.roadLength != null && projData.roadLength !== '' ? Number(projData.roadLength) : null,
          physical:              0,
          priority:              false,
          remarks:               '',
          createdBy:             this.currentUser()?.uid ?? null,
          executant: {
            name:    projData.executantName    ?? '',
            contact: projData.executantContact ?? '',
          },
          projectPhotos: { before: beforeUrls, during: duringUrls, after: afterUrls },
          photoDates: {
            before: projData.photoDateBefore || null,
            during: projData.photoDateDuring || null,
            after:  projData.photoDateAfter  || null,
          },
        };
        if (updatedPayload['hmPriority'] && updatedPayload['recurringIntervention']) {
          alert('HM Priority and Recurring Intervention cannot both be enabled.');
          this.isSubmitting.set(false);
          return;
        }

        await this.approvalService.withdrawRequest(this.editingPendingRequestId);
        await this.approvalService.enqueueChange({
          targetCollection: 'projects',
          operation: 'create',
          payload: updatedPayload,
          remarks: 'Updated pending project request',
        });
        this.closeModal();
      } catch (error: any) {
        const code = error?.code ? `[${error.code}] ` : '';
        const msg  = error?.message ?? String(error) ?? 'Could not update the pending request. Please try again.';
        console.error('Error updating pending request:', code, msg, error);
        alert(`${code}${msg}`);
      } finally {
        this.isSubmitting.set(false);
        this.isUploadingPhotos.set(false);
      }
      return;
    }

    // ── Edit-mode branch: route to updateProject (enqueues EDIT approval) ──
    if (this.isEditing() && this.editingLiveProjectId) {
      if (this.projectForm.invalid) {
        this.projectForm.markAllAsTouched();
        return;
      }
      this.isSubmitting.set(true);
      try {
        const projData = this.projectForm.getRawValue();

        // Upload new multi-photos (before/during/after) and merge with existing URLs
        const beforeUrls = [...this.photoBeforeExisting()];
        const duringUrls = [...this.photoDuringExisting()];
        const afterUrls  = [...this.photoAfterExisting()];
        const allNewPhotos = [
          ...this.photoBeforeFiles().map(f => ({ cat: 'before' as const, f })),
          ...this.photoDuringFiles().map(f => ({ cat: 'during' as const, f })),
          ...this.photoAfterFiles().map(f => ({ cat: 'after'  as const, f })),
        ];
        if (allNewPhotos.length) {
          this.isUploadingPhotos.set(true);
          this.photoUploadProgress.set(0);
          const liveId = this.editingBaseline?.['projectId'] || this.editingLiveProjectId;
          for (let i = 0; i < allNewPhotos.length; i++) {
            const { cat, f } = allNewPhotos[i];
            const url = await this.photoUploadService.uploadPhoto(`${liveId}/${cat}`, f);
            if (cat === 'before') beforeUrls.push(url);
            else if (cat === 'during') duringUrls.push(url);
            else afterUrls.push(url);
            this.photoUploadProgress.set(Math.round(((i + 1) / allNewPhotos.length) * 100));
          }
          this.isUploadingPhotos.set(false);
        }

        const updates: Record<string, any> = {
          name:                  projData.projectName,
          dept:                  projData.deptName,
          scheme:                projData.schemeName,
          loc:                   projData.location,
          block:                 projData.location,
          gp:                    projData.gp   ?? '',
          yearOfCreation:        projData.yearOfCreation ?? '',
          cost:                  Number(projData.costCr)  || 0,
          spent:                 Number(projData.spentCr) || 0,
          status:                projData.status,
          hmPriority:            !!projData.hmPriority,
          recurringIntervention: !!projData.recurringIntervention,
          roadLength: projData.roadLength != null && projData.roadLength !== '' ? Number(projData.roadLength) : null,
          executant: {
            name:    projData.executantName    ?? '',
            contact: projData.executantContact ?? '',
          },
          projectPhotos: { before: beforeUrls, during: duringUrls, after: afterUrls },
          photoDates: {
            before: projData.photoDateBefore || null,
            during: projData.photoDateDuring || null,
            after:  projData.photoDateAfter  || null,
          },
        };
        if (updates['hmPriority'] && updates['recurringIntervention']) {
          alert('HM Priority and Recurring Intervention cannot both be enabled.');
          this.isSubmitting.set(false);
          return;
        }
        await this.projectService.updateProject(this.editingLiveProjectId, updates);
        this.closeModal();
      } catch (error: any) {
        const code = error?.code ? `[${error.code}] ` : '';
        const msg  = error?.message ?? String(error) ?? 'Could not submit the edit. Please try again.';
        console.error('Error submitting edit request:', code, msg, error);
        alert(`${code}${msg}`);
      } finally {
        this.isSubmitting.set(false);
        this.isUploadingPhotos.set(false);
      }
      return;
    }

    // ── Create-mode branch ──────────────────────────────────────────────────
    const newSchemeMode = this.showNewSchemeForm();
    if (newSchemeMode) {
      this.schemeForm.markAllAsTouched();
      this.projectForm.markAllAsTouched();
      if (this.schemeForm.invalid || this.projectForm.invalid) return;
    } else if (this.projectForm.invalid) {
      this.projectForm.markAllAsTouched();
      return;
    }

    // ── Duplicate check: same project name already exists in this block/GP ──
    const rawValue = this.projectForm.getRawValue();
    const duplicate = this.liveProjects().some(p =>
      (p.name ?? '').trim().toLowerCase() === (rawValue.projectName ?? '').trim().toLowerCase() &&
      (p.loc ?? p.block ?? '') === rawValue.location &&
      (p.gp ?? '') === (rawValue.gp ?? '')
    );
    if (duplicate) {
      alert('A project with this name already exists in the selected Block/GP. Please use a different name or check the existing project.');
      return;
    }

    this.isSubmitting.set(true);
    try {
      const projData   = this.projectForm.getRawValue();
      const schemeData = this.schemeForm.value;

      const finalSchemeName = newSchemeMode
        ? schemeData.name
        : (this.pendingSchemeName() ?? projData.schemeName);

      // ── Step 1: Enqueue scheme creation if needed ─────────────────────
      if (newSchemeMode) {
        await this.approvalService.enqueueChange({
          targetCollection: 'schemes',
          operation: 'create',
          payload: {
            name:      schemeData.name,
            dept:      projData.deptName,
            totalCost: Number(schemeData.totalCost) || 0,
            spent:     Number(schemeData.spent) || 0,
          },
          remarks: 'New scheme creation request (bundled with project)',
        });
      }

      // ── Step 2: Reserve project ID ────────────────────────────────────
      const idResult = await this.projectIdService.reserveNextProjectId(projData.deptName);

      // ── Step 3: Upload before/during/after photos ─────────────────────
      const beforeUrls: string[] = [];
      const duringUrls: string[] = [];
      const afterUrls:  string[] = [];
      const allPhotos = [
        ...this.photoBeforeFiles().map(f => ({ cat: 'before' as const, f })),
        ...this.photoDuringFiles().map(f => ({ cat: 'during' as const, f })),
        ...this.photoAfterFiles().map(f => ({ cat: 'after'  as const, f })),
      ];
      if (allPhotos.length) {
        this.isUploadingPhotos.set(true);
        this.photoUploadProgress.set(0);
        for (let i = 0; i < allPhotos.length; i++) {
          const { cat, f } = allPhotos[i];
          const url = await this.photoUploadService.uploadPhoto(`${idResult.projectId}/${cat}`, f);
          if (cat === 'before') beforeUrls.push(url);
          else if (cat === 'during') duringUrls.push(url);
          else afterUrls.push(url);
          this.photoUploadProgress.set(Math.round(((i + 1) / allPhotos.length) * 100));
        }
        this.isUploadingPhotos.set(false);
      }

      // ── Step 4: Enqueue project creation ─────────────────────────────
      const createPayload: Record<string, any> = {
        name:                  projData.projectName,
        dept:                  projData.deptName,
        scheme:                finalSchemeName,
        projectId:             idResult.projectId,
        departmentCode:        idResult.departmentCode,
        sequenceNumber:        idResult.sequenceNumber,
        loc:                   projData.location,
        block:                 projData.location,
        gp:                    projData.gp ?? '',
        yearOfCreation:        projData.yearOfCreation ?? '',
        cost:                  Number(projData.costCr)  || 0,
        spent:                 Number(projData.spentCr) || 0,
        status:                projData.status,
        hmPriority:            !!projData.hmPriority,
        recurringIntervention: !!projData.recurringIntervention,
        roadLength:            projData.roadLength != null && projData.roadLength !== '' ? Number(projData.roadLength) : null,
        physical:              0,
        priority:              false,
        remarks:               '',
        createdBy:             this.currentUser()?.uid ?? null,
        executant: {
          name:    projData.executantName    ?? '',
          contact: projData.executantContact ?? '',
        },
        projectPhotos: { before: beforeUrls, during: duringUrls, after: afterUrls },
        photoDates: {
          before: projData.photoDateBefore || null,
          during: projData.photoDateDuring || null,
          after:  projData.photoDateAfter  || null,
        },
        start: new Date().toISOString().split('T')[0],
        end:   new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      };

      await this.projectService.createProject(createPayload as any);
      this.closeModal();
    } catch (error: any) {
      const code = error?.code ? `[${error.code}] ` : '';
      const msg  = error?.message ?? String(error) ?? 'Could not submit. Please try again.';
      console.error('Error submitting request:', code, msg, error);
      alert(`${code}${msg}`);
    } finally {
      this.isSubmitting.set(false);
      this.isUploadingPhotos.set(false);
    }
  }
}
